/* goldCardCatalog.js — the CARD-DISCOVERY layer for every surface that wants to
   show REAL gold drug cards outside the game (the landing page today; anything
   else that needs a logged-out card face tomorrow).

   This module is the entire seam between such a surface and the game's card
   registries. A consumer imports `goldCardProps` / `BUILD_ROWS` /
   `GOLD_CARD_SLUGS` from here and nothing else from data/, so there is exactly
   one place that knows what a gold card IS and which cards belong together.

   ── THE DISCOVERY LAW (the whole point of this file) ────────────────────────
   Every list below is DERIVED by iterating a live registry export. There is no
   literal drug roster, no re-typed ability name, no copied effect prose, no
   hand-picked icon and no hardcoded pairing anywhere in this file. A 4th
   KEYSTONE, a new EFFECT_AUGMENT entry or a new cross-card mention inside a
   ledger detail must all appear with ZERO edits here.

   What that does NOT promise, so nobody reads more into it than it says: a 33rd
   gold card becomes RENDERABLE the moment the generator sees it (goldCardProps
   returns a bag for it), but it is not automatically REACHABLE on a page,
   because BUILD_ROWS only models the two relationships the registries declare —
   a keystone and its augmented cards, and a mutually-referencing pair. A card
   that joins neither is a card with no stated relationship to another, and this
   module will not invent one for it. The only literal
   strings in this file are structural chrome: the row-id prefixes and the
   two-character joiner in a pair row's label. If you find yourself typing a drug
   name, an ability name, a cost, a colour, a Feather glyph or a sentence of
   effect copy here, stop — that is the exact failure this module exists to
   prevent. In particular: DO NOT list which cards pair with which. The pair rows
   are found by reading the ledger prose, so a new mutual reference authored in
   cardLedgers.js becomes a row on its own.

   ── THE REGISTRIES IT ITERATES ──────────────────────────────────────────────
   generated/goldCards.json  the 32 gold cards' generic_name / drug_class / cost
                             / effect, extracted from
                             backend/services/golden_cards_data.json by
                             frontend/scripts/generate-goldcards.mjs. The ONLY
                             source of ability names, drug classes and costs;
                             frontend/src has none of them of its own.
   cardLedgers.js            LEDGERS + getCardLedger — the canonical green/red
                             chips. Also the prose that the pair discovery reads.
   effectDrugMap.js          EFFECT_DRUG_SLUG (effect id -> slug), EFFECT_AUGMENT
                             (the authored keystone-synergy copy),
                             effectNodeColor (the drug's skill-tree node colour),
                             drugDisplayName.
   keystoneCards.js*         KEYSTONES (row order + identity) and
                             getKeystonePillCardProps (icon, type tag, accent).

   ── MIRROR THE CALL SITE, NOT JUST THE DATA ─────────────────────────────────
   `goldCardProps(slug)` returns the prop bag for the FACE-mode call site the
   shop uses (PlayerShop.jsx:233-241): `card` plus `accentColor`. It deliberately
   does NOT carry `owned`, `actionButton` or `requirement` — those three are
   PLAYER STATE, and a logged-out surface has none. It also does not carry
   `showCost`; that is the consumer's framing choice, exactly as it is the shop's.
   Keystone rows additionally spread getKeystonePillCardProps(id), which is what
   gives a keystone its real icon, its "Keystone" type tag and its own accent —
   the same helper PlayerShop spreads.

   ── HONESTY RULES ───────────────────────────────────────────────────────────
   - `goldCardProps` returns NULL for a slug with no goldCards.json entry OR no
     ledger, so a caller can never render a card whose body would read "No effect
     details recorded for this card yet." (PillCard.jsx:186). Nothing here
     invents copy to fill that gap; the card simply does not exist for us.
   - That null is also the ONLY exclusion mechanism. `rifampin` drops out of the
     Vancomycin row because it has no goldCards.json entry (its card lives in
     seed_golden_reservoir.py, see effectDrugMap.js:55-58) and no LEDGERS entry,
     not because anything here names it.
   - A build row's `lines` are the union of the authored EFFECT_AUGMENT lines of
     the cards that SURVIVED that filter, so a row never advertises copy about a
     card it cannot show.
*/

import GOLD_CARDS from './generated/goldCards.json';
import LEDGERS, { getCardLedger } from './cardLedgers';
import { EFFECT_DRUG_SLUG, EFFECT_AUGMENT, effectNodeColor, drugDisplayName } from './effectDrugMap';
import { KEYSTONES, getKeystonePillCardProps } from './keystoneCards';

const CARDS = GOLD_CARDS.cards || {};

/* The same normalization the generator applies to generic_name and that
   cardLedgers.js applies to a LEDGERS key, so a caller may pass either shape
   ("Sodium Bicarbonate", "sodium-bicarbonate", "sodium_bicarbonate"). */
function normalizeSlug(value) {
  return String(value || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
}

/* ── goldCardProps ──────────────────────────────────────────────────────────
   The FACE-mode prop bag. `card` mirrors the SHOP ENTRY shape (the backend card
   object PlayerShop hands straight to PillCard): drug_slug + generic_name +
   drug_class + cost + effect. `effect.ledger` is set explicitly from
   getCardLedger so PillCard prefers the canonical chips (PillCard.jsx:170,
   "A backend-authored effect.ledger, if it ever exists, wins") instead of
   re-deriving them from generic_name — the two agree today, and passing it
   makes the card's honesty independent of that lookup.

   `drug_slug` is what PillCard's drugNodeGlyph keys off (PillCard.jsx:24), so
   setting it is what makes each card draw its OWN skill-tree node glyph. No
   caller ever picks an icon. */
export function goldCardProps(slug) {
  const key = normalizeSlug(slug);
  const entry = CARDS[key];
  if (!entry) return null;
  const ledger = getCardLedger(key);
  if (!ledger) return null;
  return {
    card: {
      drug_slug: key,
      generic_name: entry.generic_name,
      drug_class: entry.drug_class,
      cost: entry.cost,
      // No `is_starter`: the generator does not emit it. See the note there —
      // the seed disagrees with the live reservoir about which cards are
      // starters, and the starter feature itself is off pending a redesign.
      effect: { ...entry.effect, ledger },
    },
    accentColor: effectNodeColor(key),
  };
}

/* Every slug the catalog can actually render, in goldCards.json key order
   (the generator sorts its keys, so this list is stable across regenerations). */
export const GOLD_CARD_SLUGS = Object.keys(CARDS).filter((slug) => goldCardProps(slug) !== null);

// ─── KEYSTONE ROWS ─────────────────────────────────────────────────────────

/* A keystone's synthetic surfacing effect id, DISCOVERED in EFFECT_DRUG_SLUG:
   the entry whose value is this keystone's slug and whose id carries the
   registry's documented `*_keystone` suffix (effectDrugMap.js:12-14, the same
   suffix test compendiumData.js:232 uses). Returns undefined for a keystone with no
   surfacing effect, which is harmless: the id only feeds PillCard's
   getEffectIcon fallback, and getKeystonePillCardProps always supplies an
   iconOverride that wins ahead of it (PillCard.jsx:406). */
function keystoneEffectId(keystoneSlug) {
  const found = Object.entries(EFFECT_DRUG_SLUG)
    .find(([effectId, slug]) => slug === keystoneSlug && effectId.endsWith('_keystone'));
  return found ? found[0] : undefined;
}

/* The keystone's own card object. Built from the KEYSTONES descriptor, which
   keystoneCards.jsx declares to be the "single source of truth for the visual
   identity of Vancomycin / Adalimumab / Metformin gold cards" — so the row label
   and the card face read the same ability name instead of disagreeing.

   `cost` is read off the keystone's goldCards.json entry when it has one, so the
   number is never typed. `effect.type` is deliberately ABSENT: the descriptor
   does not own one, PillCard's own `effect?.type || 'combat'` default supplies
   the root class, and the visible type tag comes from getKeystonePillCardProps'
   typeLabel/typeMode ('Keystone' / 'gold'). Inventing a type here would be
   inventing game content. */
function keystoneCard(k) {
  const entry = CARDS[normalizeSlug(k.id)];
  return {
    drug_slug: k.id,
    generic_name: k.genericName,
    drug_class: k.drugClass,
    ...(entry ? { cost: entry.cost } : {}),
    effect: {
      // `keystone_<id>`, the id EVERY real site builds (KeystonePickModal.jsx:58,
      // compendiumData.js:249, and SkillTreeNodeToast per PillCard.jsx:309-313). It is
      // NOT the `<id>_keystone` spelling EFFECT_DRUG_SLUG uses for the synthetic
      // surfacing effect: PillCard.jsx:273 tests `effect?.id === 'keystone_metformin'`
      // to drive the beat/heal animations, so the reverse spelling silently
      // disables them.
      id: `keystone_${k.id}`,
      name: k.abilityName,
      type: 'combat',
      // NOTHING ELSE, and this is the whole point: KeystonePickModal.jsx:56-64,
      // the app's only real FACE-mode keystone call site, passes exactly
      // {id, name, type} and no body at all. Mirroring it is what keeps this
      // card the card the game draws.
      //
      // What was here before, and why each field had to go:
      //   description/pros/cons — the KEYSTONE descriptor's, which no face-mode
      //     site passes. `disableHover` only sets visibility:hidden
      //     (PillCard.css:114-118), so the hover panel still took layout: the
      //     192-char metformin description made its card 188px against 133-149
      //     for every sibling, and flex stretch pushed that onto three innocent
      //     cards. Measured, not assumed (align-items unpinned, fault asserted).
      //   ledger — getCardLedger(k.id) returns the GOLD CARD's ledger for that
      //     drug, a DIFFERENT mechanic: cardLedgers.js:131-135 documents
      //     metformin's as the `insulin_sensitizer` passive ("+1 token per drug
      //     game"), not the Guideline Oracle heal economy. It put two unrelated
      //     mechanics on one card, and a non-null effect.ledger suppresses the
      //     legacy pros/cons rows (PillCard.jsx:170-177,196), so it also hid the
      //     keystone's own authored drawbacks.
    },
  };
}

/* Group EFFECT_AUGMENT by keystone_slug, resolving each effect id to its drug
   slug through EFFECT_DRUG_SLUG. Registry (insertion) order is preserved, and
   a slug that appears twice merges its lines rather than producing two cards. */
function augmentsByKeystone() {
  const byKeystone = new Map();
  Object.entries(EFFECT_AUGMENT).forEach(([effectId, augment]) => {
    const slug = EFFECT_DRUG_SLUG[effectId];
    if (!slug || !augment || !augment.keystone_slug) return;
    if (!byKeystone.has(augment.keystone_slug)) byKeystone.set(augment.keystone_slug, new Map());
    const bucket = byKeystone.get(augment.keystone_slug);
    const held = bucket.get(slug) || [];
    bucket.set(slug, [...held, ...(augment.lines || [])]);
  });
  return byKeystone;
}

/* Rows in KEYSTONES order, so a 4th keystone takes its place in the sequence
   without an edit. A keystone whose augment cards ALL fail goldCardProps (or
   that has no augments at all) produces no row: a build with no cards is not a
   build, and rendering an empty one would be a hole rather than a fact. */
function buildKeystoneRows() {
  const byKeystone = augmentsByKeystone();
  return KEYSTONES.map((k) => {
    const bucket = byKeystone.get(k.id);
    if (!bucket) return null;
    const kept = [...bucket.entries()]
      .map(([slug, lines]) => ({ slug, lines, props: goldCardProps(slug) }))
      .filter((c) => c.props !== null);
    if (!kept.length) return null;
    const lines = [];
    kept.forEach((c) => c.lines.forEach((line) => {
      if (!lines.includes(line)) lines.push(line);
    }));
    /* NOT passed to the cards as PillCard's `keystoneBonus`, and the reason is
       worth recording because it looks like the more faithful choice and is not.
       `keystoneBonus` renders inside `.pill-card__hover`, which `disableHover`
       and the default swap layout leave at `visibility:hidden` — it still TAKES
       LAYOUT (PillCard.css:114-118). Measured: attaching it grew every drug card
       from ~133px to roughly 3x that, with a dead band down the middle of all
       ten. The app avoids this with `sideDetails`, which slides the panel out to
       the side and keeps the card a fixed size — a floating panel that would
       overlap its neighbours in a flex row on a static page nobody hovers.
       renderStatusText, the decorator PillCard runs these lines through
       (PillCard.jsx:527), was tried too and is also wrong here: it SUBSTITUTES
       GLYPHS FOR DRUG NAMES, so "under Vancomycin" renders as "under [sigma]".
       Legible in combat, gibberish on the page that meets a visitor first.
       So the lines stay plain page prose. Faithful-to-the-game governs the
       ASSETS; the prose around them still has to read to someone who has never
       played. Both alternatives are recorded at the Landing.jsx call site. */
    return {
      id: `keystone-${k.id}`,
      kind: 'keystone',
      label: k.abilityName,
      accent: k.accent,
      lines,
      // `disableHover` mirrors KeystonePickModal.jsx:71, the app's only real
      // FACE-mode keystone call site: a keystone card there never opens a body.
      // Every real keystone BODY in the app is a `keystoneSections` two-part
      // panel (nodeDetailLayout.js:36-46, compendiumData.js:277) built from
      // beatsBody/effectsBody/beatLedger/healLedger — a surface this row does
      // not render, so it must not render a half-built stand-in either.
      keystoneProps: {
        card: keystoneCard(k),
        ...(getKeystonePillCardProps(k.id) || {}),
        disableHover: true,
      },
      cardProps: kept.map((c) => c.props),
    };
  }).filter(Boolean);
}

// ─── PAIR ROWS ─────────────────────────────────────────────────────────────

/* A card's authored ledger prose, both chips, as plain strings. This is the
   text the pair discovery reads: cardLedgers.js is where one card's behaviour is
   stated in terms of another's, so it is where a partnership is legible. */
function ledgerDetails(slug) {
  const entry = LEDGERS[slug];
  if (!entry) return [];
  // The WORD travels with the DETAIL. A ledger chip is rendered as the two
  // together (word + revealed detail), so a detail on its own is a sentence
  // fragment with its subject missing.
  return [entry.green, entry.red]
    .filter((chip) => chip && typeof chip.detail === 'string' && chip.detail.trim().length > 0)
    .map((chip) => ({ word: chip.word, detail: chip.detail }));
}

/* One side of a pair, stated as a whole clause: the chip's own word, then what
   it does. `Healing: -90% received, -10% with Folic Acid` rather than the bare
   `-90% received, ...`, which reads as contradicting its partner's line. */
function pairLine(from, to, refs) {
  const chip = refs.get(from).get(to);
  if (!chip) return '';
  return chip.word ? `${chip.word}: ${chip.detail}` : chip.detail;
}

/* Whole-word, case-insensitive match on a drug's DISPLAY name, so "Folic Acid"
   inside a sentence is a hit and a name that merely happens to be a substring of
   a longer word is not. drugDisplayName is the same title-caser the tooltips
   use, so the needle is exactly the name a reader would type. */
function displayNameMatcher(slug) {
  const name = drugDisplayName(slug).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`\\b${name}\\b`, 'i');
}

/* Which OTHER renderable gold cards does this card's ledger prose name, and in
   which detail string. Built once for every card, so the mutual test below is a
   pure set operation over discovered facts. */
function buildReferenceMap() {
  const matchers = new Map(GOLD_CARD_SLUGS.map((slug) => [slug, displayNameMatcher(slug)]));
  const refs = new Map();
  GOLD_CARD_SLUGS.forEach((slug) => {
    const details = ledgerDetails(slug);
    const hits = new Map();
    GOLD_CARD_SLUGS.forEach((other) => {
      if (other === slug) return;
      const matcher = matchers.get(other);
      const hit = details.find((chip) => matcher.test(chip.detail));
      if (hit) hits.set(other, hit);
    });
    refs.set(slug, hits);
  });
  return refs;
}

/* A PAIR is two cards whose ledgers name EACH OTHER. Nothing here knows which
   two those are: authoring a new mutual mention in cardLedgers.js mints a new
   row, and deleting one retires it. Within a row the cards keep LEDGERS
   authoring order, which is also the order the relationship reads in (the card
   that owns the drawback before the card that rescues it). */
function buildPairRows() {
  const refs = buildReferenceMap();
  const order = Object.keys(LEDGERS);
  const rank = (slug) => {
    const i = order.indexOf(slug);
    return i === -1 ? Number.MAX_SAFE_INTEGER : i;
  };
  const rows = [];
  const seen = new Set();
  GOLD_CARD_SLUGS.forEach((slug) => {
    refs.get(slug).forEach((_detail, other) => {
      if (!refs.get(other) || !refs.get(other).has(slug)) return;
      const [a, b] = rank(slug) <= rank(other) ? [slug, other] : [other, slug];
      const pairKey = `${a}\u0000${b}`;
      if (seen.has(pairKey)) return;
      seen.add(pairKey);
      rows.push({
        id: `pair-${a}-${b}`,
        kind: 'pair',
        label: `${drugDisplayName(a)} + ${drugDisplayName(b)}`,
        accent: effectNodeColor(a),
        // Each card's own statement about the other, in card order. The chip's
        // WORD is carried with its DETAIL: a ledger chip reads as "Healing" +
        // "-90% received, -10% with Folic Acid", so the detail alone starts
        // mid-clause and two of them read as contradicting each other.
        lines: [pairLine(a, b, refs), pairLine(b, a, refs)],
        keystoneProps: null,
        cardProps: [goldCardProps(a), goldCardProps(b)],
      });
    });
  });
  rows.sort((x, y) => x.id.localeCompare(y.id));
  return rows;
}

// ─── ASSEMBLY ──────────────────────────────────────────────────────────────

/* Keystone builds first (a keystone is the loadout's spine), then the pairings
   that stand on their own. Both halves are grown from their registries, so the
   only thing this line fixes is the ORDER OF THE TWO KINDS. */
export const BUILD_ROWS = [...buildKeystoneRows(), ...buildPairRows()];

export default BUILD_ROWS;
